import { Platform, ScrollView, View } from "@-/com.npmjs.react-native";
import { ScopedVariables } from "@-/com.npmjs.uniwind";
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "@-/com.npmjs.react-native";
import Animated, { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { slideViewPosition } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import { getSlidesComponents } from "@-/dev.improve.haniverse.assets.slides/components";
import { ScrollToBottomButton } from "@-/dev.improve.haniverse.components.ui.scroll-to-bottom-button";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { SlidePageDots } from "#slide-page-dots";
import {
  type SlidePageBlockMeasurement,
  type SlidePageBlockRender,
  SlidePageBlockRenderContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-page-block";
import {
  SlideLessonPageContext,
  useGoToSlidePage,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._context";
import {
  LearnCardSurfaceContext,
  LearnFlashcardSurfaceReadyMarker,
  type LearnCardSlideViews,
  useLearnCardSurface,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { paginateSlideBlocks } from "@-/dev.improve.haniverse.lib.slide.pages";
import { getSlideTextSizeCSSVariables } from "@-/dev.improve.haniverse.lib.slide.text-size";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps, ComponentType, ReactNode } from "react";
import useLatest from "@-/com.npmjs.use-latest";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

import { useCardScrollOptIn } from "@-/dev.improve.haniverse.lib.cards.review-swipe";
import { slideMdxComponents } from "#slide-mdx-components";

const PAGE_PADDING_Y = 16;
/** Keep this in sync with the MDX wrapper and the page scroll container (`gap-4`). */
const BLOCK_GAP = 16;
/** Two 2pt borders and the 64pt feedback row sit outside the scroll body. */
const PAGE_CHROME_HEIGHT = 68;
/**
 * A 16pt inset and 8pt gap leave 8pt of each adjacent slide visible inside the card; the slideshow
 * reaches through the stage's 9pt gutter, so that edge runs on to the screen's.
 */
const PAGE_INSET_X = 16;
const PAGE_GAP = 8;

type SlideProps = { components?: Record<string, unknown> };

/** One rendering of the slide, scaled to the learner's slide text size. */
function SlideBody({
  render,
  Slide,
  textSizeVariables,
}: {
  render: SlidePageBlockRender;
  Slide: ComponentType<SlideProps>;
  textSizeVariables: ComponentProps<typeof ScopedVariables>["variables"];
}) {
  return (
    // The slide text size (picked from the Learn toolbar's More menu) rescales every `text-*` size
    // inside the slide body only, so the rest of the app keeps its proportions.
    <ScopedVariables variables={textSizeVariables}>
      <SlidePageBlockRenderContext value={render}>
        <Slide components={slideMdxComponents} />
      </SlidePageBlockRenderContext>
    </ScopedVariables>
  );
}

/**
 * One page's scroll body. A page normally fits the card (that is how it was cut), so it only
 * scrolls when a single block is taller than the card; a tap opts the card into scrolling, and
 * the current page shows the scroll-down affordance meanwhile.
 */
function SlideScrollPage({ children, isCurrent }: { children: ReactNode; isCurrent: boolean }) {
  const { enableScroll, isScrollEnabled } = useCardScrollOptIn();
  const scrollViewRef = useRef<ScrollView>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setViewportHeight(event.nativeEvent.layout.height);
  }, []);
  const handleContentSizeChange = useCallback((_width: number, height: number) => {
    setContentHeight(height);
  }, []);
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrollY(event.nativeEvent.contentOffset.y);
  }, []);
  const handleScrollToBottom = useCallback(() => {
    enableScroll();
    scrollViewRef.current?.scrollTo({
      animated: true,
      y: Math.min(contentHeight - viewportHeight, scrollY + viewportHeight * 0.75),
    });
  }, [enableScroll, contentHeight, viewportHeight, scrollY]);

  const overflows = contentHeight > viewportHeight + 1;
  // Once scrolling is enabled, keep the content clear of the affordance.
  const hasMoreBelow = isCurrent && !isScrollEnabled && viewportHeight > 0 && overflows;

  return (
    <View className="relative min-h-0 flex-1">
      <ScrollView
        bounces={isScrollEnabled && overflows}
        className="flex-1"
        // Start each slide at the top; a long block keeps its beginning reachable.
        contentContainerClassName="grow gap-4 p-4"
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        onScroll={handleScroll}
        ref={scrollViewRef}
        scrollEnabled={isScrollEnabled && overflows}
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={isScrollEnabled && overflows}
      >
        {children}
      </ScrollView>
      {hasMoreBelow ? <ScrollToBottomButton onPress={handleScrollToBottom} /> : null}
    </View>
  );
}

/** Feedback belongs to each slide, stays selected when paging, and reaches the analytics sink. */
function SlideFeedback({ pageIndex, slideSlug }: { pageIndex: number; slideSlug: string }) {
  const [rating, setRating] = useState<"down" | "up" | null>(null);
  const rate = (value: "down" | "up") => {
    const next = rating === value ? null : value;
    setRating(next);
    captureAnalyticsEvent("slide_feedback_submitted", {
      page_index: pageIndex,
      rating: next,
      slide_slug: slideSlug,
    });
  };
  return (
    <View className="h-16 flex-row items-center justify-end gap-1 px-4" testID="slide-feedback">
      {(["down", "up"] as const).map((value) => (
        <Button
          accessibilityLabel={value === "up" ? "Helpful slide" : "Unhelpful slide"}
          accessibilityState={{ selected: rating === value }}
          className="size-11"
          key={value}
          onPress={() => rate(value)}
          size="icon"
          testID={`slide-feedback-${value}`}
          variant="ghost"
        >
          <View className={value === "down" ? "rotate-180" : undefined}>
            <Icon
              className={rating === value ? "text-primary" : "text-muted-foreground"}
              name="LuThumbsUp"
              size={24}
            />
          </View>
        </Button>
      ))}
    </View>
  );
}

/** A bordered slide in a horizontal slideshow; its neighbors remain visible at the edges. */
function SlideshowSlide({
  children,
  index,
  isCurrent,
  views,
}: {
  children: ReactNode;
  index: number;
  isCurrent: boolean;
  views: LearnCardSlideViews;
}) {
  const style = useAnimatedStyle(() => {
    const position = slideViewPosition(views);
    return { transform: [{ translateX: (index - position) * views.width.get() }] };
  });
  return (
    <Animated.View
      accessibilityElementsHidden={!isCurrent}
      className="absolute inset-x-4 inset-y-0 overflow-hidden rounded-xs border-2 border-border bg-surface"
      importantForAccessibility={isCurrent ? "auto" : "no-hide-descendants"}
      pointerEvents={isCurrent ? "box-none" : "none"}
      style={style}
      testID={isCurrent ? "learn-slide-page-current" : undefined}
    >
      {children}
    </Animated.View>
  );
}

/**
 * The slide's views. Only the view on show is interactive and visible to accessibility. The footer
 * authored with `<SlideFooter>` follows the last view. Until the views are cut, nothing shows,
 * unless the slide turned out to have no blocks to page (`isUnpaged`), which then renders whole.
 */
function Slideshow({
  currentPage,
  footer,
  isUnpaged,
  pageRenders,
  slideBodyProps,
  slideSlug,
  views,
}: {
  currentPage: number;
  footer: ReactNode;
  isUnpaged: boolean;
  pageRenders: SlidePageBlockRender[] | null;
  slideBodyProps: Omit<ComponentProps<typeof SlideBody>, "render">;
  slideSlug: string;
  views: LearnCardSlideViews;
}) {
  const surface = useLearnCardSurface();
  const inactiveSurface = useMemo(
    () => ({ ...surface, frontVisibleSignal: undefined, isActive: false }),
    [surface],
  );
  const pageCount = pageRenders?.length ?? 1;

  if (pageRenders === null) {
    return isUnpaged ? (
      <SlideshowSlide index={0} isCurrent views={views}>
        <SlideScrollPage isCurrent>
          <SlideBody render={null} {...slideBodyProps} />
          {footer}
        </SlideScrollPage>
        <SlideFeedback pageIndex={0} slideSlug={slideSlug} />
      </SlideshowSlide>
    ) : null;
  }
  return pageRenders.map((render, index) => {
    const isCurrent = index === currentPage;
    return (
      <SlideshowSlide
        index={index}
        isCurrent={isCurrent}
        // Views are cut afresh whenever a measurement changes; keyed by position, a view that keeps
        // its place keeps its scroll.
        key={index}
        views={views}
      >
        {/* Only the view on show is the learner's live surface (a glyph plays once it is). */}
        <LearnCardSurfaceContext value={isCurrent ? surface : inactiveSurface}>
          <SlideScrollPage isCurrent={isCurrent}>
            <SlideBody render={render} {...slideBodyProps} />
            {index === pageCount - 1 ? footer : null}
          </SlideScrollPage>
          <SlideFeedback pageIndex={index} slideSlug={slideSlug} />
        </LearnCardSurfaceContext>
      </SlideshowSlide>
    );
  });
}

/**
 * The slide card's body. A slide too long for one glance is split into pages: a hidden copy of the
 * slide measures every block at the card's width and the learner's text size, and
 * `paginateSlideBlocks` cuts them into pages (see `@-/dev.improve.haniverse.lib.slide.pages`).
 * Each page renders the slide again, showing only its own blocks.
 *
 * The pages form a horizontal slideshow with neighboring slide edges visible. Dots above the
 * slideshow show where the learner is; Undo returns a slide. Until the
 * last view, the deck's swipe and Next move on to the next view; only on the last view do they
 * dismiss the whole card.
 */
export function SlideLessonPageContent() {
  const [slideSlug, slideFooterContent, currentPage, setPageCount, views] = useContextSelectors(
    SlideLessonPageContext,
    (c) => c.slideSlug,
    (c) => c.slideFooterContent,
    (c) => c.currentPage,
    (c) => c.setPageCount,
    (c) => c.views,
  );
  const goToPage = useGoToSlidePage();
  const Slide = getSlidesComponents()[slideSlug];
  const [slideTextSize] = useDeviceSetting("slideTextSize");
  const textSizeVariables = useMemo(
    () => getSlideTextSizeCSSVariables(slideTextSize, Platform.OS === "web" ? "web" : "native"),
    [slideTextSize],
  );

  const [frame, setFrame] = useState({ height: 0, width: 0 });
  const handleFrameLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height, width } = event.nativeEvent.layout;
      views.width.set(Math.max(0, width - 2 * PAGE_INSET_X + PAGE_GAP));
      setFrame((previous) =>
        previous.height === height && previous.width === width ? previous : { height, width },
      );
    },
    [views],
  );

  // Every block's latest measurement, by block number. Blocks re-measure on any layout change
  // (text size, card size, a glyph finishing loading), and the pages follow.
  const [measurements, setMeasurements] = useState<ReadonlyMap<number, SlidePageBlockMeasurement>>(
    () => new Map(),
  );
  const measurementsRef = useLatest(measurements);
  const handleMeasure = useCallback((measurement: SlidePageBlockMeasurement) => {
    setMeasurements((previous) => {
      const known = previous.get(measurement.block);
      if (
        known !== undefined &&
        known.height === measurement.height &&
        known.count === measurement.count
      ) {
        return previous;
      }
      return new Map(previous).set(measurement.block, measurement);
    });
  }, []);
  const measureRender = useMemo<SlidePageBlockRender>(
    () => ({ kind: "measure", onMeasure: handleMeasure }),
    [handleMeasure],
  );
  // A slide compiled without block wrappers (or with no blocks at all) measures nothing. Once the
  // measuring copy has laid out with no block reporting by the next frame, show it unpaged.
  const [isUnpaged, setIsUnpaged] = useState(false);
  const handleMeasureLayout = useCallback(() => {
    safeRequestAnimationFrame(() => {
      setIsUnpaged(measurementsRef.current.size === 0);
    }, "slide-lesson-unpaged-measure");
  }, [measurementsRef]);

  const pages = useMemo(() => {
    const measured = [...measurements.values()];
    const count = measured[0]?.count;
    if (count === undefined || frame.height === 0 || measured.length < count) {
      return null;
    }
    return paginateSlideBlocks({
      blocks: measured.filter((measurement) => measurement.block < count),
      gap: BLOCK_GAP,
      pageHeight: frame.height - 2 * PAGE_PADDING_Y - PAGE_CHROME_HEIGHT,
    });
  }, [frame.height, measurements]);
  const pageCount = pages?.length ?? 1;

  useEffect(() => {
    setPageCount(pageCount);
  }, [pageCount, setPageCount]);
  // A re-measure that drops pages (a larger card, a smaller text size) keeps the learner in range.
  // Only once the pages are cut: a slide reopened on a later view must not fall back to the first
  // while it measures.
  const isCut = pages !== null;
  useEffect(() => {
    if (isCut && currentPage > pageCount - 1) {
      goToPage(pageCount - 1);
    }
  }, [currentPage, goToPage, isCut, pageCount]);

  const pageRenders = useMemo(
    () =>
      (pages ?? []).map((blocks, index): SlidePageBlockRender => ({
        blocks: new Set(blocks),
        isFirstPage: index === 0,
        kind: "page",
      })),
    [pages],
  );
  const surface = useLearnCardSurface();
  const inactiveSurface = useMemo(
    () => ({ ...surface, frontVisibleSignal: undefined, isActive: false }),
    [surface],
  );
  if (Slide === undefined) {
    throw new Error(`No slide registered for slug "${slideSlug}".`);
  }

  const isPaged = pageCount > 1;
  const slideBodyProps = { Slide, textSizeVariables };

  return (
    // The slideshow clips at the screen edge, not the card's: it extends over the stage's 9pt
    // gutters, and its padding keeps the slides where the card is.
    <View
      className="relative -mx-2.25 min-h-0 flex-1 overflow-hidden bg-background px-2.25 pt-10 pb-2"
      testID="learn-slide-content"
    >
      <View
        className="relative min-h-0 flex-1"
        onLayout={handleFrameLayout}
        // Names the slide card on show (only its tree is accessible), so E2E flows can tell it
        // from the next one without matching authored copy.
        testID={`learn-slide-${slideSlug}`}
      >
        <View
          accessibilityElementsHidden
          className="absolute inset-x-4 top-0 border-2 border-transparent px-4 opacity-0"
          importantForAccessibility="no-hide-descendants"
          onLayout={handleMeasureLayout}
          pointerEvents="none"
          testID="learn-slide-measure"
        >
          {/* Never the learner's live surface: nothing here plays, focuses or publishes. */}
          <LearnCardSurfaceContext value={inactiveSurface}>
            {frame.width > 0 ? <SlideBody render={measureRender} {...slideBodyProps} /> : null}
          </LearnCardSurfaceContext>
        </View>
        <Slideshow
          currentPage={currentPage}
          footer={slideFooterContent}
          isUnpaged={isUnpaged}
          pageRenders={pages === null ? null : pageRenders}
          slideBodyProps={slideBodyProps}
          slideSlug={slideSlug}
          views={views}
        />
      </View>
      {isPaged ? <SlidePageDots count={pageCount} currentPage={currentPage} views={views} /> : null}
      {/* Only once the pages are cut, so the deck never uncovers a card that is still measuring. */}
      {pages !== null || isUnpaged ? <LearnFlashcardSurfaceReadyMarker /> : null}
    </View>
  );
}
